import React, { useEffect, useState } from 'react';
import { AlertCircle, AlertTriangle, Check, DollarSign, Trash2, X } from 'lucide-react';
import { OnCallPayInfo, OnCallServiceOrderOption, OnCallTrade } from '../../api/onCallPay';
import {
  initialOnCallSo,
  initialOnCallTrade,
  ON_CALL_TRADE_LABEL,
  parseOnCallAmount,
  tradeForOnCallSo,
} from './onCallPayModel';

/**
 * 2020CA / 2020NV weekly On Call $ — amount + Sage coding (TAS-475/476).
 *
 * Opened from the review console's On Call $ tile: fresh (amount typed in the tile, nothing
 * saved yet), to change a saved entry, or read-only once the week is Payroll Approved.
 * Amount, service order and trade go out together in one PUT through `onSave`; nothing is
 * written until Save. Rendered INLINE under `.etrc` like MiscTaskCodeModal (no portal);
 * Escape closes via a window listener.
 *
 * The trade follows the picked SO's derived trade until the user touches the toggle; it is
 * required, so Save stays disabled while none is picked (tub-type SOs, non-job).
 *
 * Layout-stable by design: the SO list scrolls in a fixed-height box and every conditional
 * line (amount problem, no-SO note, trade hint, save error) owns a reserved slot toggled with
 * `visibility`, so clicking or typing never resizes the modal or moves what was just clicked.
 */

const TRADES: OnCallTrade[] = ['PLUMBING', 'HVAC'];
const ONCALL_COLOR = '#0e7c86';
const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "Sep 30" — the console's short-date style for a `YYYY-MM-DD` day. */
function fmtShortDate(dateStr: string): string {
  const d = new Date(dateStr + 'T12:00:00Z');
  if (isNaN(d.getTime())) return '';
  return `${MONTH_ABBR[d.getUTCMonth()]} ${d.getUTCDate()}`;
}

export interface OnCallPayModalProps {
  employeeName: string;
  weekLabel: string;
  /** Pre-filled amount: the tile's typed amount (fresh) or the saved one. */
  initialAmount: number | null;
  /** The saved entry; null when the week has none yet. */
  saved: OnCallPayInfo | null;
  /** The week's service orders, newest first (plus the saved SO if it has dropped off). */
  options: OnCallServiceOrderOption[];
  /** Preview cost code per trade (the saved `costCode` wins while the coding matches it). */
  costCodes: Record<OnCallTrade, string>;
  payType: number;
  max: number;
  /** Payroll Approved: everything disabled, no Save / Remove. */
  readOnly: boolean;
  onClose: () => void;
  /** One PUT; resolves to an error message, or null on success (the modal then closes). */
  onSave: (amount: number | null, serviceOrderID: number | null, trade: OnCallTrade | null) => Promise<string | null>;
}

const OnCallPayModal: React.FC<OnCallPayModalProps> = ({
  employeeName, weekLabel, initialAmount, saved, options, costCodes, payType, max, readOnly, onClose, onSave,
}) => {
  const [amountText, setAmountText] = useState(initialAmount != null ? initialAmount.toFixed(2) : '');
  const [so, setSo] = useState<number | null>(() => initialOnCallSo(options, saved));
  const [trade, setTrade] = useState<OnCallTrade | null>(() => initialOnCallTrade(options, saved));
  /** Set once the user picks a trade by hand — SO changes stop re-defaulting it. */
  const [tradeTouched, setTradeTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !saving) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, saving]);

  const parsed = parseOnCallAmount(amountText, max);
  const amount = parsed.ok ? parsed.amount : null;
  const amountError = !parsed.ok
    ? parsed.error
    : parsed.amount == null
      ? (saved ? 'Enter an amount, or use Remove On Call to clear it.' : 'Enter an amount.')
      : null;
  const disabled = readOnly || saving;
  const canSave = !disabled && amount != null && trade != null;
  const weekHasSos = options.some((o) => o.lastWorkDate);

  const pickSo = (next: number | null) => {
    if (disabled) return;
    setSo(next);
    if (!tradeTouched) setTrade(tradeForOnCallSo(next, options, saved));
  };

  const pickTrade = (next: OnCallTrade) => {
    if (disabled) return;
    setTrade(next);
    setTradeTouched(true);
  };

  // Sage coding preview — the saved coding is authoritative while the picks still match it.
  const savedSo = saved ? saved.serviceOrderID ?? null : undefined;
  const matchesSaved = saved != null && so === savedSo && trade === (saved.trade ?? null);
  const soOpt = so == null ? undefined : options.find((o) => o.serviceOrderID === so);
  const jobAlt = matchesSaved ? (saved?.jobAltID ?? soOpt?.jobAltID ?? null) : (soOpt?.jobAltID ?? null);
  const costCode = matchesSaved && saved?.costCode ? saved.costCode : (trade ? costCodes[trade] : null);
  const shownPayType = (matchesSaved ? saved?.payType : null) ?? payType;

  const run = async (amt: number | null, nextSo: number | null, nextTrade: OnCallTrade | null) => {
    setSaving(true);
    setSaveError(null);
    const err = await onSave(amt, nextSo, nextTrade);
    if (err) {
      setSaveError(err);
      setSaving(false);
      return;
    }
    onClose();
  };

  const handleSave = () => {
    if (!canSave) return;
    if (saved && amount === saved.amount && so === savedSo && trade === saved.trade) { onClose(); return; }
    void run(amount, so, trade);
  };

  const handleRemove = () => {
    if (disabled || !saved) return;
    void run(null, null, null);
  };

  const renderSoRow = (
    key: string,
    value: number | null,
    body: React.ReactNode,
  ) => {
    const selected = so === value;
    return (
      <label key={key} className={`etrc-ocm-so${selected ? ' is-selected' : ''}${disabled ? ' is-disabled' : ''}`}>
        <input
          type="radio"
          name="etrc-ocm-so"
          checked={selected}
          disabled={disabled}
          onChange={() => pickSo(value)}
        />
        <span className="etrc-ocm-radio" aria-hidden="true">{selected && <Check size={11} />}</span>
        {body}
      </label>
    );
  };

  return (
    <div className="etrc-overlay" onClick={saving ? undefined : onClose}>
      <div
        className="etrc-mw-modal etrc-ocm-modal"
        role="dialog"
        aria-modal="true"
        aria-label="On Call $"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="etrc-mw-header">
          <div className="etrc-mw-icon-wrap etrc-ocm-icon-wrap">
            <DollarSign size={22} color={ONCALL_COLOR} />
          </div>
          <div>
            <div className="etrc-mw-title">On Call $</div>
            <div className="etrc-mw-subtitle">{employeeName} · {weekLabel}</div>
          </div>
          <button type="button" className="etrc-mw-close" onClick={onClose} disabled={saving} title="Close">
            <X size={18} />
          </button>
        </div>

        <div className="etrc-mw-body">
          <div className="etrc-mtc-field">
            <label htmlFor="etrc-ocm-amount">Amount</label>
            <span className={`etrc-prod-amt lg${amount != null ? ' filled' : ''}${readOnly ? ' ro' : ''}`}>
              <span className="cur">$</span>
              <input
                id="etrc-ocm-amount"
                type="text"
                inputMode="decimal"
                value={amountText}
                readOnly={readOnly}
                disabled={saving}
                autoFocus={!readOnly && initialAmount == null}
                onChange={(e) => setAmountText(e.target.value)}
                onFocus={(e) => { if (!readOnly) e.currentTarget.select(); }}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleSave(); } }}
              />
            </span>
            {/* One reserved line: the amount problem when there is one, else the cap. */}
            <div
              className={`etrc-ocm-slot${amountError ? ' etrc-ocm-slot-warn' : ''}`}
              style={{ visibility: readOnly ? 'hidden' : 'visible' }}
              title={amountError ?? undefined}
            >
              {amountError ?? `Max $${Math.round(max).toLocaleString('en-US')} per week`}
            </div>
          </div>

          <div className="etrc-mtc-field">
            <label id="etrc-ocm-so-label">Service order</label>
            <div className="etrc-ocm-solist" role="radiogroup" aria-labelledby="etrc-ocm-so-label">
              {options.map((o) => renderSoRow(String(o.serviceOrderID), o.serviceOrderID, (
                <span className="etrc-ocm-so-main">
                  <span className="etrc-ocm-so-top">
                    <span className="etrc-ocm-so-no">SO {o.altServiceOrderID}</span>
                    {o.derivedTrade && <span className="etrc-ocm-chip">{ON_CALL_TRADE_LABEL[o.derivedTrade]}</span>}
                    {o.lastWorkDate && <span className="etrc-ocm-so-date">Last worked {fmtShortDate(o.lastWorkDate)}</span>}
                  </span>
                  <span className="etrc-ocm-so-job">
                    {o.jobAltID
                      ? <>Job {o.jobAltID}{o.jobName ? ` · ${o.jobName}` : ''}</>
                      : (o.name ?? 'No job')}
                  </span>
                </span>
              )))}
              {renderSoRow('none', null, (
                <span className="etrc-ocm-so-main">
                  <span className="etrc-ocm-so-top"><span className="etrc-ocm-so-no">No service order</span></span>
                  <span className="etrc-ocm-so-job">Non-job</span>
                </span>
              ))}
            </div>
            <div className="etrc-ocm-slot" style={{ visibility: weekHasSos ? 'hidden' : 'visible' }}>
              No service orders were punched this week.
            </div>
          </div>

          <div className="etrc-mtc-field">
            <label id="etrc-ocm-trade-label">Trade</label>
            <div className="etrc-ocm-seg" role="radiogroup" aria-labelledby="etrc-ocm-trade-label">
              {TRADES.map((t) => (
                <button
                  key={t}
                  type="button"
                  role="radio"
                  aria-checked={trade === t}
                  className={trade === t ? 'is-on' : undefined}
                  disabled={disabled}
                  onClick={() => pickTrade(t)}
                >
                  {ON_CALL_TRADE_LABEL[t]}
                </button>
              ))}
            </div>
            <div
              className="etrc-ocm-slot etrc-ocm-slot-warn"
              role="note"
              style={{ visibility: trade == null ? 'visible' : 'hidden' }}
            >
              <AlertTriangle size={12} />
              <span>
                {readOnly
                  ? 'No trade on file — not coded for the Sage export.'
                  : 'Pick Plumbing or HVAC to save — it sets the Sage cost code.'}
              </span>
            </div>
          </div>

          <div
            className={`etrc-oncall-coding${matchesSaved ? '' : ' is-preview'}`}
            title={matchesSaved ? 'Sage coding as saved' : 'Sage coding preview — not saved yet'}
          >
            Sage: Pay type {shownPayType} · {jobAlt ? `Job ${jobAlt}` : 'No job'} · Cost code {costCode ?? '—'}
          </div>

          {/* Reserved two-line slot so a failed save never pushes the footer down. */}
          <div
            className="etrc-mtc-error etrc-ocm-error"
            role="alert"
            title={saveError ?? undefined}
            style={{ visibility: saveError ? 'visible' : 'hidden' }}
          >
            <AlertCircle size={15} />
            <span>{saveError}</span>
          </div>
        </div>

        <div className="etrc-mw-footer">
          {!readOnly && saved && (
            <button type="button" className="etrc-btn danger etrc-ocm-remove" onClick={handleRemove} disabled={saving}>
              <Trash2 size={14} />Remove On Call
            </button>
          )}
          <button type="button" className="etrc-btn" onClick={onClose} disabled={saving}>
            {readOnly ? 'Close' : 'Cancel'}
          </button>
          {!readOnly && (
            <button type="button" className="etrc-btn primary etrc-ocm-save" onClick={handleSave} disabled={!canSave}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default OnCallPayModal;
